import { useMutation } from '@apollo/client';

import { Profile } from '../../Types';
import { mergeFeedbackIntoContactDetails } from '../../Util/Form/helpers';
import { ContactStatusOptionsObject } from '../../api/CoachingForm/schema';
import {
  DROPDOWN_TYPES,
  FormSaveType,
} from '../../Util/Form/formData.constants';
import { getLocaleString } from '../../translations';
import { OUTLIER_INGRESS } from '../../PerformancePortal/FormLibrary/utils/helper';
import { Feature, isFeatureEnabled } from '../../config/FeaturesConfig';
import {
  ContactDetailsInput,
  GoodRecommendationOptionsObject,
} from '../../api/CoachingNotes/schema';
import {
  UPDATE_FORM_REVIEW,
  WRITE_OUTLIER_EXCEPTION,
} from '../../api/FormLibrary/mutations';
import { buildFormAnswers } from '../../Util/Form/dartFormAnswerBuilders';

export interface FormSubmissionOptions {
  formType: string;
  ingress: string | null;
  profile: Profile;
  cscEntityPrefix: string;
  requesterUserID: string;
}

export interface SubmitFormParams {
  formState: any; // Current form state with all answers
  templateData: any; // Form template structure
  contact: any; // Contact being reviewed
  feedbackDetails?: ContactDetailsInput; // Optional feedback from modal
  saveType: FormSaveType; // DRAFT or DELIVERED
  emailChecked: boolean; // Whether to send email notification
  contactSummary?: string; // Manager summary text (optional)
  creationTimestamp?: string; // First review timestamp
}

export const useFormSubmission = (options: FormSubmissionOptions) => {
  const [updateFormReview, { loading, error }] =
    useMutation(UPDATE_FORM_REVIEW);
  const [writeOutlierException] = useMutation(WRITE_OUTLIER_EXCEPTION);

  const submitForm = async ({
    formState,
    templateData,
    contact,
    feedbackDetails,
    saveType,
    emailChecked,
    contactSummary,
    creationTimestamp,
  }: SubmitFormParams) => {
    // Build form answers (extracted to separate utility)
    const formAnswers = buildFormAnswers({
      dropdownItems: formState.dropdownItems,
      questionsAnswered: formState.questionsAnswered,
      templateData,
      contactSummary,
      formType: options.formType,
    });

    const formDetailsInput = {
      formID: templateData.FORM_ID,
      version: templateData.VERSION,
      formAnswers,
      contactStatus: saveType,
    };

    const contactDetailsInput = mergeFeedbackIntoContactDetails(
      feedbackDetails,
      contact,
      formState.dropdownItems.find(d =>
        d.type.includes(DROPDOWN_TYPES.VERTICAL)
      )?.selected,
      formDetailsInput,
      options.ingress ?? ''
    );

    // Extract justification for outlier exception before removing from contactDetailsInput
    const justificationForOutlier = contactDetailsInput.justification;

    // Remove justification field as it's not part of the API schema for updateFormReview
    // (it's used for outlier management but not sent to updateFormReview)
    if (contactDetailsInput.justification !== undefined) {
      delete contactDetailsInput.justification;
    }

    const updateFormReviewInput = {
      userName: options.profile.user.userName,
      userID: contact.userID,
      year: contact.calYear,
      week: contact.calWeek,
      contactID: contact.contactID,
      reviewStatus: ContactStatusOptionsObject.REVIEWED,
      contactDetailsInput,
      isReviewedByCSA: false,
      contactStatus: saveType,
      formDetails: [formDetailsInput],
      inAppFlag: saveType === FormSaveType.DELIVERED,
      emailFlag: emailChecked,
      locale: getLocaleString(),
      creationTimestamp,
    };

    // Submit form
    const result = await updateFormReview({
      variables: {
        input: updateFormReviewInput,
      },
    });

    // Handle outlier management if needed
    if (
      options.ingress === OUTLIER_INGRESS &&
      isFeatureEnabled(Feature.OUTLIER_MANAGEMENT) &&
      contactDetailsInput.goodRecommendation ===
        GoodRecommendationOptionsObject.NO &&
      saveType === FormSaveType.DELIVERED
    ) {
      await writeOutlierException({
        variables: {
          userId: options.requesterUserID,
          deliveredToUsername: options.profile.user.userName,
          deliveredToUserId: contact.userID,
          contactId: contact.contactID,
          outlierCalYear: contact.calYear,
          outlierCalWeek: contact.calWeek,
          justification: justificationForOutlier,
        },
      });
    }

    return result;
  };

  return {
    submitForm,
    loading,
    error,
  };
};
